/**
 * AnalyticsService
 * Servicio puro para gestionar eventos de análisis y seguimiento (Google Analytics, localStorage)
 * Sin dependencias del componente React ni this
 */

export class AnalyticsService {
  /**
   * Envía evento de agendamiento de cita médica a Google Analytics
   * @param {Object} userInfo - Información del usuario (Nautcli, RecienAutenticado, TipoIdentificacion)
   * @param {string} specialtyName - Nombre de la especialidad seleccionada
   * @param {string} step - Paso del flujo (default: 'Paso 2 Seleccion usuario y especialidad')
   */
  static sendAppointmentSchedulingEvent(userInfo, specialtyName, step = 'Paso 2 Seleccion usuario y especialidad') {
    if (!window.dataLayer) {
      console.warn('❌ dataLayer no disponible para analytics');
      return;
    }

    dataLayer.push({
      event: 'agendar_cita_medica',
      user_id: userInfo?.Nautcli,
      log_status: userInfo?.RecienAutenticado,
      login_type: userInfo?.TipoIdentificacion === 1 ? 'Personas' : 'Empresas',
      affiliation: 'Salud',
      bread_crumbs: 'servicios-médicos',
      especialidad: specialtyName,
      step: step,
    });
  }

  /**
   * Guarda el nombre de la especialidad seleccionada en localStorage
   * @param {string} specialtyName - Nombre de la especialidad
   */
  static setSpecialtyInStorage(specialtyName) {
    try {
      localStorage.setItem('especialidad', specialtyName);
    } catch (error) {
      console.warn('⚠️ No se pudo guardar especialidad en localStorage:', error);
    }
  }

  /**
   * Orquesta el tracking completo de agendamiento de cita
   * @param {Object} userInfo - Información del usuario
   * @param {string} specialtyName - Nombre de la especialidad
   * @param {string} step - Paso del flujo (opcional)
   */
  static trackAppointmentScheduling(userInfo, specialtyName, step = 'Paso 2 Seleccion usuario y especialidad') {
    this.sendAppointmentSchedulingEvent(userInfo, specialtyName, step);
    this.setSpecialtyInStorage(specialtyName);
  }

  /**
   * Envía evento de búsqueda de cita médica
   * @param {Object} userInfo - Información del usuario
   * @param {string} specialtyName - Especialidad seleccionada
   * @param {string} location - Ubicación/sede seleccionada
   */
  static trackAppointmentSearch(userInfo, specialtyName, location) {
    if (!window.dataLayer) return;

    dataLayer.push({
      event: 'agendar_cita_medica',
      user_id: userInfo?.Nautcli,
      log_status: userInfo?.RecienAutenticado,
      login_type: userInfo?.TipoIdentificacion === 1 ? 'Personas' : 'Empresas',
      affiliation: 'Salud',
      bread_crumbs: 'servicios-médicos',
      option: 'Buscar cita',
      sede: location,
      especialidad: specialtyName,
      step: 'Paso 5 Busqueda cita',
    });
  }

  /**
   * Envía evento de selección de fecha
   * @param {Object} userInfo - Información del usuario
   * @param {string} specialtyName - Especialidad seleccionada
   * @param {string} location - Ubicación/sede seleccionada
   */
  static trackDateSelection(userInfo, specialtyName, location) {
    if (!window.dataLayer) return;

    dataLayer.push({
      event: 'agendar_cita_medica',
      user_id: userInfo?.Nautcli,
      log_status: userInfo?.RecienAutenticado,
      login_type: userInfo?.TipoIdentificacion === 1 ? 'Personas' : 'Empresas',
      affiliation: 'Salud',
      bread_crumbs: 'servicios-médicos',
      sede: location,
      especialidad: specialtyName,
      step: 'Paso 4 Seleccion fecha',
    });
  }

  /**
   * Envía evento de incumplimiento pedagógico
   * @param {Object} userInfo - Información del usuario
   * @param {string} buttonName - Nombre del botón clicado
   */
  static trackPedagogicNonComplianceAction(userInfo, buttonName) {
    if (!window.dataLayer) return;

    dataLayer.push({
      event: 'incumplimiento_pedagogico',
      user_id: userInfo?.Nautcli,
      log_status: userInfo?.RecienAutenticado,
      login_type: userInfo?.TipoIdentificacion === 1 ? 'Personas' : 'Empresas',
      affiliation: 'Salud',
      bread_crumbs: 'servicios-médicos',
      option: buttonName,
      step: 'Paso 1',
    });
  }

  /**
   * Envía evento cuando el usuario ya tiene cita existente
   * @param {Object} userInfo - Información del usuario
   * @param {string} buttonOption - Opción del botón clicado (Cerrar|Próxima cita)
   */
  static trackExistentAppointmentEvent(userInfo, buttonOption) {
    if (!window.dataLayer) return;

    dataLayer.push({
      event: 'agendar_cita_medica',
      user_id: userInfo?.Nautcli,
      log_status: userInfo?.RecienAutenticado,
      login_type: userInfo?.TipoIdentificacion === 1 ? 'Personas' : 'Empresas',
      affiliation: 'Salud',
      bread_crumbs: 'servicios-médicos',
      option: buttonOption,
      step: 'Ya posee cita',
    });
  }
}
